import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db, googleProvider } from "../firebase";
import { createUserWithEmailAndPassword, sendEmailVerification, onAuthStateChanged, signInWithPopup } from "firebase/auth";
import { doc, setDoc, addDoc, collection, getDoc, query, where, getDocs } from "firebase/firestore";
import AlertError from "./PermissionDenied";
import { useUser } from "../userContext"; // Import the useUser hook
import { Transition } from "@headlessui/react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { 
    SparklesIcon, 
    CheckCircleIcon, 
    TrophyIcon, 
    ChartBarIcon,
    UsersIcon,
    RocketLaunchIcon,
    CurrencyDollarIcon,
    BoltIcon,
    InformationCircleIcon,
    EyeIcon, 
    EyeSlashIcon, 
    XCircleIcon, 
    ExclamationTriangleIcon,
    ShieldCheckIcon,
    UserGroupIcon, 
    BuildingOffice2Icon,
    ShoppingBagIcon,
    ComputerDesktopIcon,
    AcademicCapIcon,
    HeartIcon,
    BriefcaseIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { validateSignupForm } from '../utils/signupValidation';


const Signup = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [notice, setNotice] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const { loading: userLoading, userProfile, businessProfile } = useUser(); // Access the loading state from userContext
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [showPersonalFields, setShowPersonalFields] = useState(true);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [honeypot, setHoneypot] = useState(''); // Spam protection
    const [formStartTime, setFormStartTime] = useState(Date.now()); // Timing protection
    const [validationErrors, setValidationErrors] = useState({}); // Field-specific errors
    const [currentIndustryIndex, setCurrentIndustryIndex] = useState(0);
    const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true); // Track if auto-advance should continue

    const industries = [
        {
            icon: <UserGroupIcon className="h-6 w-6" />,
            title: "Digital Marketing Agencies",
            example: "Brand Logo Guessing Game",
            description: "Hint game revealing famous logos piece by piece - 'Guess this SaaS company!' Perfect for LinkedIn posts that get B2B audiences competing."
        },
        {
            icon: <ComputerDesktopIcon className="h-6 w-6" />,
            title: "SaaS Companies", 
            example: "Software Tool Memory Challenge",
            description: "List game where players name all the tools in a category - 'Name every project management tool!' Post on LinkedIn to engage your target audience."
        },
        {
            icon: <ShoppingBagIcon className="h-6 w-6" />,
            title: "E-commerce Brands",
            example: "Store Location Pin Drop", 
            description: "Map game where players pin your store locations - 'Can you find all our flagship stores?' Great for driving foot traffic and brand awareness."
        },
        {
            icon: <AcademicCapIcon className="h-6 w-6" />,
            title: "Event Companies",
            example: "Conference Speaker Guessing Game",
            description: "Hint game revealing keynote speakers - 'Guess this industry expert!' Share on LinkedIn to build pre-event excitement."
        },
        {
            icon: <BuildingOffice2Icon className="h-6 w-6" />,
            title: "Professional Services",
            example: "Industry Terminology Challenge", 
            description: "List game where players name terms in your field - 'List every type of business contract!' Position yourself as the expert."
        },
        {
            icon: <HeartIcon className="h-6 w-6" />,
            title: "Non-Profit Organizations",
            example: "Global Impact Map Challenge",
            description: "Pin drop game showing where your organization works - 'Can you find all the communities we serve?' Perfect for donor education."
        },
        {
            icon: <BriefcaseIcon className="h-6 w-6" />,
            title: "Recruiters & Talent Acquisition", 
            example: "Tech Company Logo Hunt",
            description: "Hint game revealing startup logos with clues. Create shareable LinkedIn content that gets professionals competing and talking."
        }
    ];

    // Auto-advance carousel every 7 seconds (slower) - only if auto-advance is enabled
    useEffect(() => {
        if (!autoAdvanceEnabled) return;
        
        const interval = setInterval(() => {
            setCurrentIndustryIndex((prev) => (prev + 1) % industries.length);
        }, 7000);
        return () => clearInterval(interval);
    }, [autoAdvanceEnabled]);

    const nextIndustry = () => {
        setAutoAdvanceEnabled(false); // Stop auto-advance when user manually navigates
        setCurrentIndustryIndex((prev) => (prev + 1) % industries.length);
    };

    const prevIndustry = () => {
        setAutoAdvanceEnabled(false); // Stop auto-advance when user manually navigates
        setCurrentIndustryIndex((prev) => (prev - 1 + industries.length) % industries.length);
    };

    const currentIndustry = industries[currentIndustryIndex];

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user && !isProcessing && !userLoading && userProfile && businessProfile) {
                // If the user is logged in and user data is fully loaded, navigate
                navigate("/studio/dashboard");
            }
        });

        return () => unsubscribe();
    }, [navigate, isProcessing, userLoading, userProfile, businessProfile]);

    useEffect(() => {
        setNotice("");
        setValidationErrors({}); // Clear validation errors when fields change
    }, [email, password, confirmPassword, firstName, lastName, companyName]);

    useEffect(() => {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        setIsEmailValid(isValidEmail);
    }, [email]);

    useEffect(() => {
        if (confirmPassword) {
            setPasswordsMatch(password === confirmPassword);
        }
    }, [password, confirmPassword]);

    const isFormValid = email && password && confirmPassword && firstName && lastName;

    const getErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/email-already-in-use':
                return 'An account already exists with this email. Please sign in instead.';
            case 'auth/invalid-email':
                return 'The email address is not valid.';
            case 'auth/operation-not-allowed':
                return 'Email/password accounts are not enabled.';
            case 'auth/weak-password':
                return 'The password is too weak.';
            case 'auth/user-disabled':
                return 'The user account has been disabled by an administrator.';
            case 'auth/user-not-found':
                return 'No user found with this email.';
            case 'auth/wrong-password':
                return 'The password is invalid.';
            case 'auth/account-exists-with-different-credential':
                return 'An account already exists with this email. Please sign in with email and password instead.';
            case 'auth/popup-closed-by-user':
                return 'Sign up was cancelled. Please try again.';
            default:
                return 'Sorry, something went wrong. Please try again.';
        }
    };

    const signupWithUsernameAndPassword = async (e) => {
        e.preventDefault();

        // Validate form data with spam prevention
        const formData = {
            email,
            firstName,
            lastName,
            companyName,
            password,
            confirmPassword,
            honeypot
        };

        const validation = validateSignupForm(formData, formStartTime);
        
        if (!validation.isValid) {
            if (validation.errors.general) {
                setNotice(validation.errors.general);
            } else {
                setValidationErrors(validation.errors);
                setNotice("Please fix the errors below and try again.");
            }
            return;
        }

        if (password === confirmPassword) {
            setIsProcessing(true);

            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;


                const businessRef = await addDoc(collection(db, "company"), {
                    ownerId: user.uid,
                    name: companyName || "",
                    createdAt: new Date(),
                });
   

                // Create user document BEFORE sending welcome email
                await setDoc(doc(db, "users", user.uid), {
                    uid: user.uid,
                    email: user.email,
                    firstName,
                    lastName,
                    companyId: businessRef.id,
                    createdAt: new Date(),
                    authProvider: "email",
                    emailVerified: false
                });
               

                // Add a small delay to ensure Firestore is consistent
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Send welcome email
                try {
                    const sendWelcomeEmail = httpsCallable(
                        functions, 
                        'sendWelcomeEmail',
                        { region: 'us-central1' } // Add this options object
                    );
                    const result = await sendWelcomeEmail({ userId: user.uid });
                 
                } catch (emailError) {
                    console.error('Welcome email error:', emailError);
                    // Don't throw here - we still want to complete signup
                }

                setTimeout(() => {
                    setIsProcessing(false);
                }, 2000);

            } catch (error) {
                console.error('Signup error:', error);
                const errorMessage = getErrorMessage(error.code);
                setNotice(errorMessage);
                setIsProcessing(false);
            }
        } else {
            setNotice("Passwords don't match. Please try again.");
            setIsProcessing(false);
        }
    };

    const handleContinueToPassword = () => {
        if (firstName && lastName) {
            setShowPersonalFields(false);
            setTimeout(() => {
                setShowPasswordFields(true);
            }, 300);
        }
    };

    const checkPasswordStrength = (password) => {
        let strength = 0;
        let feedback = [];

        if (password.length >= 8) {
            strength += 1;
        } else {
            feedback.push('At least 8 characters');
        }
        if (/[A-Z]/.test(password)) {
            strength += 1;
        } else {
            feedback.push('One uppercase letter');
        }
        if (/[a-z]/.test(password)) {
            strength += 1;
        } else {
            feedback.push('One lowercase letter');
        }
        if (/[0-9]/.test(password)) {
            strength += 1;
        } else {
            feedback.push('One number');
        }
        if (/[^A-Za-z0-9]/.test(password)) {
            strength += 1;
        } else {
            feedback.push('One special character');
        }

        setPasswordStrength(strength);
        setPasswordFeedback(feedback.join(' • '));
    };

    useEffect(() => {
        if (password) {
            checkPasswordStrength(password);
        } else {
            setPasswordStrength(0);
            setPasswordFeedback('');
        }
    }, [password]);

    const handleGoogleSignup = async () => {
        setIsProcessing(true);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            
            // Check if user document exists
            const userDoc = await getDoc(doc(db, "users", user.uid));
            
            if (!userDoc.exists()) {
                try {
                    // Extract name from Google profile
                    const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
                    const googleFirstName = nameParts[0] || '';
                    const googleLastName = nameParts.slice(1).join(' ') || '';

                    // Create business document
                    const businessRef = await addDoc(collection(db, "company"), {
                        ownerId: user.uid,
                        name: "",
                        createdAt: new Date(),
                    });

                    // Create user document
                    await setDoc(doc(db, "users", user.uid), {
                        uid: user.uid,
                        email: user.email,
                        firstName: googleFirstName,
                        lastName: googleLastName,
                        companyId: businessRef.id,
                        createdAt: new Date(),
                        authProvider: "google",
                        emailVerified: true  // Google emails are pre-verified
                    });

                    // Ensure Firebase Auth user is marked as verified
                    if (!auth.currentUser.emailVerified) {
                        await auth.currentUser.reload();
                    }

                    // Add a small delay to ensure Firestore is consistent
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    // Send welcome email
                    try {
                        const sendWelcomeEmail = httpsCallable(
                            functions,
                            'sendWelcomeEmail',
                            { region: 'us-central1' }
                        );
                        await sendWelcomeEmail({ userId: user.uid });
                    } catch (emailError) {
                        console.error('Welcome email error:', emailError);
                        // Continue with signup even if welcome email fails
                    }
                } catch (dbError) {
                    console.error("Database error:", dbError);
                    await auth.signOut();
                    setNotice("Failed to create account. Please try again or contact support.");
                    setIsProcessing(false);
                    return;
                }
            } else {
                // User exists, ensure emailVerified is set to true
                const userData = userDoc.data();
                if (!userData.emailVerified) {
                    await setDoc(doc(db, "users", user.uid), {
                        ...userData,
                        emailVerified: true
                    }, { merge: true });
                }
                // Ensure Firebase Auth user is marked as verified
                if (!auth.currentUser.emailVerified) {
                    await auth.currentUser.reload();
                }
            }
            // If user exists or was created successfully, the auth state listener will handle navigation
            setIsProcessing(false);
        } catch (error) {
            console.error('Google signup error:', error);
            let errorMessage;
            
            // Immediately stop processing for popup-related errors
            if (error.code === 'auth/popup-closed-by-user' || 
                error.code === 'auth/cancelled-popup-request' || 
                error.code === 'auth/popup-blocked') {
                setIsProcessing(false);
                switch (error.code) {
                    case 'auth/popup-closed-by-user':
                        errorMessage = "Sign-up cancelled. Please try again.";
                        break;
                    case 'auth/cancelled-popup-request':
                        errorMessage = "Only one popup can be open at a time. Please try again.";
                        break;
                    case 'auth/popup-blocked':
                        errorMessage = "Popup was blocked by your browser. Please enable popups for this site.";
                        break;
                }
                setNotice(errorMessage);
                return;
            }
            
            // Handle other errors
            switch (error.code) {
                case 'auth/account-exists-with-different-credential':
                    // With account linking enabled, this error shouldn't occur often
                    errorMessage = "An account with this email already exists. Please sign in with your email and password.";
                    if (error.email) {
                        setTimeout(() => {
                            navigate(`/login?email=${encodeURIComponent(error.email)}`);
                        }, 2000);
                    }
                    break;
                case 'auth/email-already-in-use':
                    // This might occur if the email is already in use but not yet linked
                    errorMessage = "An account with this email already exists. Please sign in with your email and password.";
                    if (error.email) {
                        setTimeout(() => {
                            navigate(`/login?email=${encodeURIComponent(error.email)}`);
                        }, 2000);
                    }
                    break;
                default:
                    errorMessage = "An error occurred during sign up. Please try again.";
            }
            
            setNotice(errorMessage);
            // Ensure we're signed out in case of error
            try {
                await auth.signOut();
            } catch (signOutError) {
                console.error("Error signing out:", signOutError);
            }
            setIsProcessing(false);
        }
    };

    // Load retro font
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Share+Tech+Mono:wght@400&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        
        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, []);

    const retroStyle = {
        fontFamily: '"Share Tech Mono", monospace',
    };

    return (
        <>
            <style jsx>{`
                .retro-gradient {
                    background: linear-gradient(to bottom, #2C3E50, #1A2530);
                }
                .feature-card {
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(241, 196, 15, 0.2);
                    background: rgba(52, 73, 94, 0.3);
                }
                .floating-icon {
                    animation: float 6s ease-in-out infinite;
                }
                .hero-title {
                    text-shadow: 2px 2px 0px #E67E22, 4px 4px 0px #D35400, 2px 2px 10px rgba(241, 196, 15, 0.5);
                    -webkit-text-stroke: 1px #E67E22;
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-15px); }
                }
                .retro-button {
                    box-shadow: 0 6px 0 #C0392B, 0 8px 15px rgba(231, 76, 60, 0.3);
                    transform: translateY(0);
                    transition: all 0.1s ease;
                }
                .retro-button:hover {
                    transform: translateY(2px);
                    box-shadow: 0 4px 0 #C0392B, 0 6px 12px rgba(231, 76, 60, 0.3);
                }
                .retro-button:active {
                    transform: translateY(6px);
                    box-shadow: 0 0px 0 #C0392B, 0 2px 8px rgba(231, 76, 60, 0.3);
                }
                .game-element {
                    animation: pulse 2s ease-in-out infinite alternate;
                }
                @keyframes pulse {
                    0% { opacity: 0.6; }
                    100% { opacity: 1; }
                }
            `}</style>
            
            <main className="min-h-screen retro-gradient" style={retroStyle}>
                {/* Clean background effects */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none">
                    {/* Simple animated stars */}
                    <div className="absolute top-10 left-10 w-1 h-1 bg-yellow-400 animate-pulse"></div>
                    <div className="absolute top-20 right-20 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '1s'}}></div>
                    <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '2s'}}></div>
                    <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute bottom-20 right-10 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '1.5s'}}></div>
                    
                    {/* Simple gaming elements */}
                    <div className="absolute top-16 right-12 text-2xl animate-pulse game-element" style={{animationDelay: '1s'}}>🎮</div>
                    <div className="absolute bottom-40 right-20 text-xl animate-pulse game-element" style={{animationDelay: '1.5s'}}>🎲</div>
                    <div className="absolute top-1/3 left-8 text-lg animate-bounce game-element" style={{animationDelay: '2.5s', animationDuration: '2.5s'}}>✨</div>
                    <div className="absolute bottom-32 left-12 text-xl animate-pulse game-element" style={{animationDelay: '0.8s'}}>🚀</div>
                    <div className="absolute top-40 right-1/3 text-purple-400 text-lg animate-bounce game-element" style={{animationDelay: '1.8s', animationDuration: '2.8s'}}>⚡</div>
                </div>

                <div className="relative flex flex-col lg:flex-row min-h-screen">
                    {/* Left Side - Form */}
                    <div className="w-full lg:w-1/2 flex flex-col relative z-10 min-h-screen lg:min-h-0">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 lg:p-8">
                            <a href="/" className="flex items-center space-x-2">
                                <div className="text-lg font-black tracking-wider" style={{
                                    color: '#F1C40F',
                                    textShadow: '1px 1px 0px #E67E22, 2px 2px 0px #D35400'
                                }}>
                                    GROUP CHAT HEROES
                                    <span className="ml-2 text-sm px-2 py-1 border-2 border-yellow-400 rounded font-bold" style={{
                                        backgroundColor: '#E74C3C',
                                        borderColor: '#F1C40F',
                                        color: '#FFFFFF',
                                        textShadow: '1px 1px 0px #C0392B'
                                    }}>STUDIO</span>
                                </div>
                            </a>
                            <div className="text-sm" style={{color: '#ECF0F1'}}>
                                Already have an account?{' '}
                                <Link to="/studio/login" className="font-bold hover:text-yellow-300 transition-colors" style={{color: '#F1C40F'}}>
                                    Sign in
                                </Link>
                            </div>
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
                            <div className="w-full max-w-md">
                                {/* Hero Section */}
                                <div className="text-center mb-8">
                                    <h1 className="text-3xl lg:text-4xl font-black mb-2 hero-title tracking-wider" style={{color: '#F1C40F'}}>
                                        JOIN THE
                                    </h1>
                                    <h1 className="text-4xl lg:text-5xl font-black mb-6 hero-title tracking-wider" style={{color: '#F1C40F'}}>
                                        STUDIO
                                    </h1>
                                                                        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border-2 border-green-400 bg-green-900/20 backdrop-blur-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                            <span className="text-sm font-bold text-green-300">
                                                7-DAY FREE TRIAL
                                            </span>
                                        </div>
                                        <div className="w-px h-4 bg-green-400/30"></div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs font-bold text-green-200">then from</span>
                                            <span className="text-lg font-black" style={{color: '#F1C40F'}}>£19</span>
                                            <span className="text-xs font-bold text-green-200">/mo</span>
                                        </div>
                                    </div>


                                </div>

                                {notice && (
                                    <div className="mb-6">
                                        <AlertError text={notice} dashboardButton={false} />
                                    </div>
                                )}

                                {!showEmailForm ? (
                                    <div className="space-y-4">
                                        <button
                                            onClick={handleGoogleSignup}
                                            disabled={isProcessing}
                                            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-700 rounded-lg font-bold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-200 disabled:opacity-50 shadow-lg"
                                        >
                                            {isProcessing ? (
                                                <div className="flex items-center">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700 mr-3"></div>
                                                    LOADING...
                                                </div>
                                            ) : (
                                                <>
                                                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                                                        <path
                                                            fill="#4285F4"
                                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                                        />
                                                        <path
                                                            fill="#34A853"
                                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                        />
                                                        <path
                                                            fill="#FBBC05"
                                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                        />
                                                        <path
                                                            fill="#EA4335"
                                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                        />
                                                    </svg>
                                                    SIGN UP WITH GOOGLE
                                                </>
                                            )}
                                        </button>

                                        <div className="relative">
                                            <div className="absolute inset-0 flex items-center">
                                                <div className="w-full border-t-2" style={{borderColor: '#34495E'}}></div>
                                            </div>
                                            <div className="relative flex justify-center text-sm">
                                                <span className="px-2 font-bold" style={{backgroundColor: '#2C3E50', color: '#ECF0F1'}}>OR</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => setShowEmailForm(true)}
                                            className="w-full flex items-center justify-center px-6 py-3 rounded-lg font-bold transition-all duration-200 retro-button"
                                            style={{
                                                backgroundColor: '#E74C3C',
                                                color: 'white'
                                            }}
                                        >
                                            SIGN UP WITH EMAIL
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={signupWithUsernameAndPassword} className="space-y-4">
                                        {/* Honeypot field - hidden from users but bots will fill it */}
                                        <input
                                            type="text"
                                            name="website"
                                            value={honeypot}
                                            onChange={(e) => setHoneypot(e.target.value)}
                                            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                                            tabIndex="-1"
                                            autoComplete="off"
                                        />
                                        
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                                                EMAIL
                                            </label>
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                autoComplete="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className={`w-full rounded-lg border-2 px-4 py-3 font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-200 ${
                                                    email && !isEmailValid 
                                                        ? 'border-red-500 bg-red-900/20 text-red-300' 
                                                        : 'border-gray-500 bg-gray-800/50 text-white focus:border-yellow-400'
                                                }`}
                                                style={{backgroundColor: '#34495E', color: '#ECF0F1'}}
                                                placeholder="you@company.com"
                                            />
                                            {email && !isEmailValid && (
                                                <p className="mt-2 text-sm font-bold text-red-400">
                                                    INVALID EMAIL FORMAT
                                                </p>
                                            )}
                                            {validationErrors.email && (
                                                <p className="mt-2 text-sm font-bold text-red-400">
                                                    {validationErrors.email.toUpperCase()}
                                                </p>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="firstName" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                                                    FIRST NAME
                                                </label>
                                                <input
                                                    id="firstName"
                                                    name="firstName"
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border-2 border-gray-500 px-4 py-3 font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200"
                                                    style={{backgroundColor: '#34495E', color: '#ECF0F1'}}
                                                    placeholder="JOHN"
                                                />
                                                {validationErrors.firstName && (
                                                    <p className="mt-1 text-xs font-bold text-red-400">
                                                        {validationErrors.firstName.toUpperCase()}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="lastName" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                                                    LAST NAME
                                                </label>
                                                <input
                                                    id="lastName"
                                                    name="lastName"
                                                    type="text"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border-2 border-gray-500 px-4 py-3 font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200"
                                                    style={{backgroundColor: '#34495E', color: '#ECF0F1'}}
                                                    placeholder="DOE"
                                                />
                                                {validationErrors.lastName && (
                                                    <p className="mt-1 text-xs font-bold text-red-400">
                                                        {validationErrors.lastName.toUpperCase()}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="companyName" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                                                COMPANY <span style={{color: '#95A5A6'}}>(OPTIONAL)</span>
                                            </label>
                                            <input
                                                id="companyName"
                                                name="companyName"
                                                type="text"
                                                value={companyName}
                                                onChange={(e) => setCompanyName(e.target.value)}
                                                className="w-full rounded-lg border-2 border-gray-500 px-4 py-3 font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200"
                                                style={{backgroundColor: '#34495E', color: '#ECF0F1'}}
                                                placeholder="ACME CORP"
                                            />
                                            {validationErrors.companyName && (
                                                <p className="mt-1 text-xs font-bold text-red-400">
                                                    {validationErrors.companyName.toUpperCase()}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="password" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                                                PASSWORD
                                            </label>
                                            <input
                                                id="password"
                                                name="password"
                                                type="password"
                                                onChange={(e) => setPassword(e.target.value)}
                                                value={password}
                                                required
                                                className="w-full rounded-lg border-2 border-gray-500 px-4 py-3 font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all duration-200"
                                                style={{backgroundColor: '#34495E', color: '#ECF0F1'}}
                                                placeholder="CREATE SECURE PASSWORD"
                                            />
                                            {password && (
                                                <div className="mt-3">
                                                    <div className="flex space-x-1 mb-2">
                                                        {[...Array(5)].map((_, index) => (
                                                            <div
                                                                key={index}
                                                                className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                                                                    index < passwordStrength 
                                                                        ? passwordStrength <= 2 
                                                                            ? 'bg-red-500' 
                                                                            : passwordStrength <= 3 
                                                                                ? 'bg-yellow-500' 
                                                                                : 'bg-green-500'
                                                                        : 'bg-gray-600'
                                                                }`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <p className={`text-sm font-bold ${
                                                        passwordStrength <= 2 
                                                            ? 'text-red-400' 
                                                            : passwordStrength <= 3 
                                                                ? 'text-yellow-400' 
                                                                : 'text-green-400'
                                                    }`}>
                                                        {passwordFeedback || 'STRONG PASSWORD ✓'}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="confirm-password" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                                                CONFIRM PASSWORD
                                            </label>
                                            <div className="relative">
                                                <input
                                                    id="confirm-password"
                                                    name="confirm-password"
                                                    type="password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                    className={`w-full rounded-lg border-2 px-4 py-3 font-bold focus:outline-none focus:ring-2 transition-all duration-200 ${
                                                        confirmPassword && !passwordsMatch 
                                                            ? 'border-red-500 focus:ring-red-500 bg-red-900/20' 
                                                            : confirmPassword && passwordsMatch
                                                                ? 'border-green-500 focus:ring-green-500 bg-green-900/20'
                                                                : 'border-gray-500 focus:ring-yellow-400 focus:border-yellow-400'
                                                    }`}
                                                    style={{backgroundColor: '#34495E', color: '#ECF0F1'}}
                                                    placeholder="CONFIRM PASSWORD"
                                                />
                                                {confirmPassword && (
                                                    <div className="absolute right-3 top-3.5">
                                                        {passwordsMatch ? (
                                                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                                        ) : (
                                                            <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {confirmPassword && (
                                                <p className={`mt-2 text-sm font-bold ${
                                                    passwordsMatch ? 'text-green-400' : 'text-red-400'
                                                }`}>
                                                    {passwordsMatch ? 'PASSWORDS MATCH ✓' : 'PASSWORDS DON\'T MATCH'}
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={!isFormValid || isProcessing}
                                            className="w-full font-bold text-white rounded-lg px-6 py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed retro-button"
                                            style={{
                                                backgroundColor: '#E74C3C'
                                            }}
                                        >
                                            {isProcessing ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                                    CREATING...
                                                </div>
                                            ) : (
                                                'START 7-DAY FREE TRIAL →'
                                            )}
                                        </button>

                                        <div className="text-center">
                                            <button
                                                type="button"
                                                onClick={() => setShowEmailForm(false)}
                                                className="text-sm font-bold hover:text-yellow-300 transition-colors"
                                                style={{color: '#F1C40F'}}
                                            >
                                                ← BACK TO GOOGLE SIGN-UP
                                            </button>
                                        </div>
                                    </form>
                                )}

                                                <p className="mt-6 text-xs text-center font-bold" style={{color: '#95A5A6'}}>
                    BY SIGNING UP, YOU AGREE TO OUR{' '}
                    <a href="/terms" className="hover:text-yellow-300" style={{color: '#F1C40F'}}>TERMS</a>
                    {' '}AND{' '}
                    <a href="/privacy" className="hover:text-yellow-300" style={{color: '#F1C40F'}}>PRIVACY POLICY</a>
                </p>

                {/* Mobile Learn More Button */}
                <div className="lg:hidden mt-6 text-center">
                    <button
                        onClick={() => {
                            const featuresSection = document.getElementById('desktop-features');
                            featuresSection?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="text-sm font-bold hover:text-yellow-300 transition-colors flex items-center gap-1 mx-auto"
                        style={{color: '#F1C40F'}}
                    >
                        Learn More About Studio Features
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>

                    {/* Right Side - Pro Business Features */}
                    <div className="w-full lg:w-1/2 text-white relative overflow-hidden feature-card" style={{backgroundColor: '#34495E'}} id="desktop-features">
                        {/* Background decoration */}
                        <div className="absolute inset-0">
                            <div className="absolute top-20 right-20 w-32 h-32 bg-yellow-400/10 rounded-full floating-icon"></div>
                            <div className="absolute bottom-32 left-16 w-24 h-24 bg-yellow-400/10 rounded-full floating-icon" style={{animationDelay: '2s'}}></div>
                            <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-yellow-400/10 rounded-full floating-icon" style={{animationDelay: '4s'}}></div>
                        </div>

                        <div className="relative z-10 flex flex-col justify-center p-6 lg:p-12 xl:p-16">
                            <div className="max-w-lg">
                                <div className="mb-8">
                                                        <h2 className="text-3xl xl:text-4xl font-black mb-4 tracking-wider hero-title" style={{color: '#F1C40F'}}>
                        TURN CONTENT
                    </h2>
                    <h2 className="text-3xl xl:text-4xl font-black mb-4 tracking-wider hero-title" style={{color: '#F1C40F'}}>
                        INTO CUSTOMERS
                    </h2>
                    <p className="text-lg leading-relaxed font-bold mb-2" style={{color: '#F39C12'}}>
                        Generate 300% more leads with interactive games
                    </p>
                    <p className="text-base leading-relaxed font-bold" style={{color: '#ECF0F1'}}>
                        Create branded experiences that turn visitors into paying customers.
                    </p>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-gray-800/30 border border-gray-600/50 rounded-lg p-4 text-center hover:border-yellow-400/50 transition-colors">
                                        <div className="flex justify-center mb-2">
                                            <SparklesIcon className="h-6 w-6" style={{color: '#F39C12'}} />
                                        </div>
                                        <h3 className="font-bold text-xs mb-1" style={{color: '#F1C40F'}}>WHITE LABEL GAMES</h3>
                                        <p className="text-xs leading-tight" style={{color: '#BDC3C7'}}>Fully customizable with your branding</p>
                                    </div>

                                    <div className="bg-gray-800/30 border border-gray-600/50 rounded-lg p-4 text-center hover:border-yellow-400/50 transition-colors">
                                        <div className="flex justify-center mb-2">
                                            <ChartBarIcon className="h-6 w-6" style={{color: '#3498DB'}} />
                                        </div>
                                        <h3 className="font-bold text-xs mb-1" style={{color: '#F1C40F'}}>ADVANCED ANALYTICS</h3>
                                        <p className="text-xs leading-tight" style={{color: '#BDC3C7'}}>Track behavior and conversion rates</p>
                                    </div>

                                    <div className="bg-gray-800/30 border border-gray-600/50 rounded-lg p-4 text-center hover:border-yellow-400/50 transition-colors">
                                        <div className="flex justify-center mb-2">
                                            <CurrencyDollarIcon className="h-6 w-6" style={{color: '#2ECC71'}} />
                                        </div>
                                        <h3 className="font-bold text-xs mb-1" style={{color: '#F1C40F'}}>CUSTOM CTAS</h3>
                                        <p className="text-xs leading-tight" style={{color: '#BDC3C7'}}>Add your own lead capture forms</p>
                                    </div>
                                </div>

                                {/* Industry-Specific Use Cases Carousel - Desktop */}
                                <div className="mt-6 pt-4 border-t" style={{borderColor: 'rgba(241, 196, 15, 0.3)'}}>
                                    <h3 className="text-center text-sm font-bold mb-4" style={{color: '#F1C40F'}}>
                                        PROVEN RESULTS ACROSS INDUSTRIES
                                    </h3>
                                    <div className="relative">
                                        <div 
                                            className="rounded-lg border-2 transition-all duration-500 overflow-hidden"
                                            style={{
                                                background: 'linear-gradient(135deg, #3498DB, #9B59B6)',
                                                border: '2px solid rgba(241, 196, 15, 0.6)',
                                                height: '240px'
                                            }}
                                        >
                                            <div className="h-full flex items-center p-8">
                                                <div className="flex items-center gap-4 w-full min-h-0">
                                                    <div className="flex-shrink-0 p-3 rounded-lg bg-white/20">
                                                        {currentIndustry.icon}
                                                    </div>
                                                    <div className="flex-1 min-h-0 flex flex-col justify-center">
                                                        <h4 className="text-base font-black text-white mb-2 leading-tight">
                                                            {currentIndustry.title.toUpperCase()}
                                                        </h4>
                                                        <h5 className="text-sm font-bold text-yellow-300 mb-3 leading-tight">
                                                            {currentIndustry.example}
                                                        </h5>
                                                        <p className="text-sm leading-relaxed text-white/90 line-clamp-4">
                                                            {currentIndustry.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Manual navigation buttons */}
                                            <div className="absolute top-1/2 -translate-y-1/2 -left-4">
                                                <button
                                                    onClick={prevIndustry}
                                                    className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-colors shadow-lg"
                                                >
                                                    <ChevronLeftIcon className="h-5 w-5 text-white" />
                                                </button>
                                            </div>
                                            <div className="absolute top-1/2 -translate-y-1/2 -right-4">
                                                <button
                                                    onClick={nextIndustry}
                                                    className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-colors shadow-lg"
                                                >
                                                    <ChevronRightIcon className="h-5 w-5 text-white" />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Progress indicators */}
                                        <div className="flex justify-center gap-2 mt-5">
                                            {industries.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentIndustryIndex(index)}
                                                    className={`w-2 h-2 rounded-full transition-all ${
                                                        index === currentIndustryIndex 
                                                            ? 'bg-yellow-400 w-8' 
                                                            : 'bg-white/30 hover:bg-white/50'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 text-center">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-green-400 bg-green-900/30">
                                        <TrophyIcon className="w-5 h-5 text-green-400" />
                                        <span className="text-sm font-black text-green-300">TRUSTED BY MARKETING TEAMS</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </main>


        </>
    );
}

export default Signup;